import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { AppDefaultStateProps } from '@store/reducers';
import { ConnectionStatus } from '../types';

export function useConnectionStatus(): ConnectionStatus {
  const [isOnline, setIsOnline] = useState<boolean>(
    typeof navigator !== 'undefined' ? navigator.onLine : true,
  );
  const [wasOffline, setWasOffline] = useState<boolean>(false);

  const mqttClient = useSelector(
    (state: AppDefaultStateProps) => state.mqtt?.clientMQTT,
  );

  const isMqttConnected = mqttClient?.connected === true;

  useEffect(() => {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
      return;
    }

    const handleOnline = () => {
      setIsOnline(true);
      if (wasOffline) {
        setTimeout(() => setWasOffline(false), 1000);
      }
    };

    const handleOffline = () => {
      setIsOnline(false);
      setWasOffline(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    setIsOnline(navigator.onLine);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [wasOffline]);

  if (!isOnline) {
    return ConnectionStatus.DISCONNECTED;
  }

  if (!mqttClient || !isMqttConnected) {
    if (mqttClient?.reconnecting) {
      return ConnectionStatus.RECONNECTING;
    }
    return ConnectionStatus.DISCONNECTED;
  }

  if (wasOffline && isOnline) {
    return ConnectionStatus.RECONNECTING;
  }

  return ConnectionStatus.CONNECTED;
}
